import { actions as buildsActions, operations as buildsOperations } from '@saucelabs/sl-module-builds';
import { recordTrack } from '@saucelabs/sl-util-segmentio';
import { utils as authUtils } from '@saucelabs/sl-container-authentication';
import { api as jobsApi, utils as jobsUtil, actions as jobActions, selectors as jobSelectors } from '@saucelabs/sl-module-jobs';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';

import * as actions from './actions';
import * as constants from './constants';
import { stopBuildSelector } from './selectors';
import { operations as rootCauseOperations, actions as rootCauseActions } from '../root-causes';

const getFromDatetime = () => Math.round(new Date().getTime() / 1000);
let instance;
let lastFromDatetime = getFromDatetime();

export const subscribeBuild = ({ id }) => (dispatch, getState) => {
  if (instance) return null;
  dispatch(actions.startSubscribeBuild());
  instance = () => {
    dispatch(buildsActions.getBuildById({ id }));
    dispatch(buildsOperations.fetchBuildUpdatedJobs(
      { id, full: true, from: lastFromDatetime },
      { skipErrorPage: true },
    ))
      .then((result) => {
        const { auth } = jobSelectors.routerQuerySelector(getState());
        const perfEnabledJobs = jobsUtil.getPerfEnabledJobs({ data: result });

        if (perfEnabledJobs?.length) {
          dispatch(jobActions.getPerfMetrics(
            jobsUtil.preparePerfMetricsParams({ auth, jobs: perfEnabledJobs }),
          ));
        }
        lastFromDatetime = getFromDatetime();
      });
    const timeoutId = setTimeout(() => instance && instance(), constants.POLLING_TIMEOUT_MS);
    dispatch(actions.saveBuildsTimeout(timeoutId));
  };
  return instance();
};

export const unsubscribeBuild = () => (dispatch) => {
  dispatch(actions.stopSubscribeBuild());
  instance = null;
};

export const fetchRecurringJobs = ({ id, skip = 0 }) => (dispatch) => (
  dispatch(buildsOperations.fetchBuildJobs({
    id, skip, full: true, limit: constants.BUILD_FETCH_OFFSET,
  }))
    .then((result) => (
      result.truncated ?
        dispatch(fetchRecurringJobs({ id, skip: skip + constants.BUILD_FETCH_OFFSET })) :
        Promise.resolve()))
);

export const initialize = ({ id }) => (dispatch, getState) => (
  Promise.all([
    dispatch(buildsOperations.fetchBuildJobs({
      id, full: true, skip: 0, limit: constants.BUILD_FETCH_OFFSET,
    })),
    dispatch(buildsActions.getBuildById({ id })),
  ]).catch((originalError) => {
    const state = getState();
    const isAuthenticatedUser = state.user.isAuthenticated();
    const isPublicBuild = state.apps.builds.container.getIn(['build', 'public']);
    const currentPathname = state.router.location.pathname;

    if (!isAuthenticatedUser && !isPublicBuild) {
      throw authUtils.redirectToMainLogin(currentPathname);
    }
    throw originalError; // This means we're logged in and the build does not exist - fall back to full error
  }).then(([jobsResponse]) => {
    dispatch(recordTrack('Automated Build Test Viewed', { id }));
    if (jobsResponse.truncated) {
      // lets fetch all build jobs first
      dispatch(fetchRecurringJobs({ id, skip: constants.BUILD_FETCH_OFFSET }))
        .then(() => dispatch(subscribeBuild({ id })));
    } else {
      // since we have all jobs fetched, we can start to update jobs
      dispatch(subscribeBuild({ id }));
    }
  }).then(() => {
    const state = getState();
    if (
      state.experiments.get(constants.FAILURE_ANALYSIS_EXPERIMENT) ===
      constants.FAILURE_ANALYSIS_EXPERIMENT
    ) {
      return dispatch(rootCauseOperations.getContribution());
    }

    return Promise.resolve();
  })
);

export const stopBuild = () => (dispatch, getState) => {
  const { buildOwner, runningJobsIds } = stopBuildSelector(getState());

  return Promise
    .all(runningJobsIds.map((id) => jobsApi.stopJob({ id, username: buildOwner })))
    .then(() => dispatch(notificationActions.notifyInfo('Stopping running tests from this build')));
};

export const cleanup = () => (dispatch) => {
  dispatch(actions.cleanup());
  dispatch(rootCauseActions.cleanup());
  dispatch(unsubscribeBuild());
};
